import React from "react";
import { useHistory } from "react-router";
import { Button } from "../components/ui/button";
import { IonContent, IonPage, IonIcon } from "@ionic/react";
import {
  checkmarkCircle,
  shareOutline,
  colorPaletteOutline,
  linkOutline,
  phonePortraitOutline,
  logoApple,
  logoGooglePlaystore,
  arrowForward,
  sparklesOutline,
  trendingUpOutline,
  shieldCheckmarkOutline,
  flashOutline
} from "ionicons/icons";

const LandingPage: React.FC = () => {
  const history = useHistory();

  const features = [
    {
      icon: colorPaletteOutline,
      title: "Design 100% Personalizzabile",
      description:
        "Colori, loghi, foto profilo e layout. Rifletti perfettamente la tua Brand Identity.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: shareOutline,
      title: "Condivisione in 1 Click",
      description:
        "QR Code, Email, WhatsApp, SMS o NFC. Scegli il metodo che preferisci.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: phonePortraitOutline,
      title: "Nessuna App Richiesta",
      description:
        "I tuoi clienti salvano i contatti direttamente dal browser. Zero barriere.",
      gradient: "from-green-500 to-teal-500",
    },
    {
      icon: linkOutline,
      title: "Tutto in Uno",
      description:
        "Social, siti web, portfolio, bio e contatti. Tutto in un unico posto.",
      gradient: "from-orange-500 to-yellow-500",
    },
  ];

  const benefits = [
    {
      icon: sparklesOutline,
      stat: "88%",
      label: "dei biglietti cartacei finisce nel cestino",
    },
    {
      icon: trendingUpOutline,
      stat: "2.5%",
      label: "aumento vendite per ogni 2.000 biglietti",
    },
    {
      icon: shieldCheckmarkOutline,
      stat: "72%",
      label: "giudica un'azienda dal suo biglietto",
    },
  ];

  const keyFeatures = [
    "Condivisione illimitata di biglietti",
    "Integrazione Social Media & Link personalizzati",
    "Salvataggio diretto del contatto in rubrica",
    "Interfaccia Personalizzabile e Moderna",
  ];

  return (
    <IonPage>
      <IonContent fullscreen className="ion-padding" style={{ "--background": "#2f2f2f" }}>
        <div className="min-h-screen relative">
          {/* Background Top */}
          <div className="absolute top-0 left-0 right-0 h-24 md:h-40 z-0 opacity-80">
            <img
              src="/assets/background-top.png"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>

          {/* Background Bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-24 md:h-40 z-0 opacity-80">
            <img
              src="/assets/background_bottom.png"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>

          {/* Animated gradient blobs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>

          {/* Main Content */}
          <div className="relative z-10 px-4 py-12 md:py-20">
            <div className="max-w-7xl mx-auto">
          
          {/* Logo */}
          <div className="flex justify-center mb-12 animate-fade-in mt-20 pt-12 border-t border-white/5" style={{ marginTop: "4.5rem" }}>
            <img
              src="/assets/Ecard-logo-nobackground.png"
              alt="ECard Logo"
              className="h-12 md:h-16 drop-shadow-2xl"
            />
          </div>

          {/* Hero Section */}
          <div className="text-center mb-20 space-y-6 animate-slide-up">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight tracking-tight">
              Il Futuro del
              <span className="block mt-2 bg-gradient-to-r from-teal-300 via-teal-600 to-teal-400 bg-clip-text text-transparent animate-gradient">
                Networking Professionale
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Crea, Personalizza e Condividi il tuo biglietto da visita digitale.
            </p>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Sviluppato con passione da <span className="text-teal-300">EffAI</span> e il gruppo <span className="text-teal-300">Fedabo</span>.
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid md:grid-cols-3 gap-6 mb-20 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="group relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 hover:border-teal-400/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-teal-500/20"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/0 to-teal-500/0 group-hover:from-teal-500/10 group-hover:to-transparent transition-all duration-500"></div>
                <div className="relative z-10">
                  <IonIcon
                    icon={benefit.icon}
                    className="text-5xl text-teal-400 mb-4 block"
                  />
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {benefit.stat}
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {benefit.label}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Problem Section */}
          <div className="max-w-4xl mx-auto mb-20 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              <span className="mt-2 text-teal-400">Tutto viene digitalizzato.</span> Non lasciare che il tuo network finisca nel cestino.
            </h2>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 inline-flex items-center justify-center gap-3">
                <IonIcon icon={flashOutline} className="text-teal-400 text-5xl" />
                Perché Scegliere ECard
              </h2>
              <p className="text-xl text-gray-400 mt-4">
                Tutto ciò che serve per fare networking in modo professionale
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 hover:border-white/30 transition-all duration-500 hover:scale-105 hover:shadow-2xl"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  <div className="relative z-10">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                      <IonIcon icon={feature.icon} className="text-3xl text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed text-lg">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About EffAI Section */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="relative overflow-hidden backdrop-blur-xl bg-gradient-to-br from-teal-500/20 via-cyan-500/20 to-blue-500/20 border border-teal-400/30 rounded-3xl p-10 md:p-16 text-center shadow-2xl">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-3 mb-8">
                  <IonIcon icon={sparklesOutline} className="text-5xl text-teal-400" />
                  <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r text-white-400 bg-clip-text">
                    Più di una Semplice App
                  </h2>
                </div>

                <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
                  ECard nasce dall'esperienza di <span className="text-teal-300 font-semibold">EffAI</span>.
                  Aiutiamo PMI e liberi professionisti a modernizzare ed efficientare i processi grazie all'AI.
                  Questa app è il nostro regalo per darti un assaggio di cosa significa lavorare in modo{" "}
                  <span className="text-teal-300 font-semibold">smart</span> ed{" "}
                  <span className="text-teal-300 font-semibold">efficiente</span>.
                </p>

                <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                  {keyFeatures.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 backdrop-blur-sm bg-black/20 rounded-xl p-4 border border-white/10 hover:border-teal-400/50 transition-all duration-300"
                    >
                      <IonIcon
                        icon={checkmarkCircle}
                        className="text-3xl text-teal-400 flex-shrink-0"
                      />
                      <p className="text-gray-200 font-medium">{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-4xl mx-auto">
            <div className="relative text-center overflow-hidden backdrop-blur-xl shadow-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 hover:border-teal-400/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-teal-500/20">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                  Fai il primo passo verso
                  <span className="block mt-2 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                    l'innovazione
                  </span>
                </h2>
                <p className="text-xl text-gray-200 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Unisciti a tutti i professionisti che hanno già scelto ECard.
                  Lascia un segno indelebile al tuo prossimo incontro.
                </p>

                <Button
                  onClick={() => history.push("/")}
                  className="group relative px-10 py-7 text-xl font-bold bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-400 text-white rounded-2xl shadow-2xl hover:shadow-teal-500/50 transition-all duration-500 hover:scale-105 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-3">
                    Inizia Gratuitamente
                    <IonIcon icon={arrowForward} className="text-2xl group-hover:translate-x-1 transition-transform" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-300 via-cyan-300 to-blue-300 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Button>

                <p className="text-sm text-gray-400 mt-6">
                  Nessuna carta di credito richiesta • Sempre gratuito
                </p>
              </div>
            </div>

            {/* Store Links */}
            <div className="mt-12 text-center">
              <p className="text-gray-400 text-lg mb-6">Disponibile anche su mobile</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <a
                  href="https://apps.apple.com/it/app/ecard/id6756520426"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 px-8 py-4 backdrop-blur-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-2xl transition-all duration-300 hover:scale-105 min-w-[220px]"
                >
                  <IonIcon icon={logoApple} className="text-4xl text-white" />
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Disponibile su</div>
                    <div className="text-lg font-semibold text-white">App Store</div>
                  </div>
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.simone.digitalcard&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 px-8 py-4 backdrop-blur-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 rounded-2xl transition-all duration-300 hover:scale-105 min-w-[220px]"
                >
                  <IonIcon icon={logoGooglePlaystore} className="text-4xl text-white" />
                  <div className="text-left">
                    <div className="text-xs text-gray-400">Disponibile su</div>
                    <div className="text-lg font-semibold text-white">Google Play</div>
                  </div>
                </a>
              </div>
            </div>
          </div>

              {/* Footer */}
              <div className="text-center mt-20 pt-12 border-t border-white/5">
                <p className="text-gray-500 text-sm">
                  © 2025 EffAI Srl SB - Tutti i diritti riservati
                </p>
                <div className="flex justify-center gap-4 mt-2">
                  <a href="https://iodized-parent-254.notion.site/Informativa-Privacy-BlueLife-SITO-294516d8da30803e8d67e6be150c41d5" className="text-gray-500 hover:text-teal-400 text-xs transition-colors">Privacy</a>
                  <span className="text-gray-700 text-xs">•</span>
                  <a href="https://iodized-parent-254.notion.site/Cookie-Policy-EffAI-294516d8da3080db82d2f1cc46d651e8" className="text-gray-500 hover:text-teal-400 text-xs transition-colors">Cookie</a>
                </div>
                
            
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LandingPage;